import React, { useState, createContext, useContext } from 'react';
export type Role = '1' | '2a' | '2b' | '2c' | '2d' | '3' | '4' | '5' | '6' | '7';
export interface User {
  id: string;
  name: string;
  role: Role;
  email: string;
}
interface RBACContextType {
  currentUser: User;
  setCurrentUser: (user: User) => void;
  canView: (module: string, field?: string) => boolean;
  canEdit: (module: string, field?: string) => boolean;
  canCreate: (module: string) => boolean;
  canDelete: (module: string) => boolean;
}
const RBACContext = createContext<RBACContextType | undefined>(undefined);
// Default user for dev
const DEFAULT_USER: User = {
  id: 'u1',
  name: 'Alessio (Admin)',
  role: '1',
  email: 'alessio@seform.it'
};
export const RBACProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [currentUser, setCurrentUser] = useState<User>(DEFAULT_USER);
  // Permission logic based on role
  const hasPermission = (action: 'V' | 'M' | 'C' | 'D', module: string, field?: string): boolean => {
    const role = currentUser.role;
    // Super-user (1) has all permissions
    if (role === '1') return true;
    // Responsabili (2a-2d) have full access mostly
    if (role.startsWith('2')) return true;
    // Specific restrictions for other roles
    // Example: Addetto Formazione (6) only views Contracts
    if (role === '6' && module === 'contracts') {
      return action === 'V';
    }
    // Default restrictive
    // Implement full matrix here based on requirements
    return true; // Temporary permissive for dev, restrict later
  };
  const canView = (module: string, field?: string) => hasPermission('V', module, field);
  const canEdit = (module: string, field?: string) => hasPermission('M', module, field);
  const canCreate = (module: string) => hasPermission('C', module);
  const canDelete = (module: string) => hasPermission('D', module);
  return <RBACContext.Provider value={{
    currentUser,
    setCurrentUser,
    canView,
    canEdit,
    canCreate,
    canDelete
  }} data-id="element-2128" data-mp="28f65zawalyan">
      {children}
    </RBACContext.Provider>;
};
export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) throw new Error('useRBAC must be used within RBACProvider');
  return context;
};